import { useState } from 'react';
import Link from 'next/link';
import { FieldValues, useForm } from 'react-hook-form';
import { contactService } from '@/api/contact';
import { Button, Typography } from '@/components/common';
import {
	InputEmail,
	InputPhone,
	InputText,
	Textarea,
	InputCheck,
} from '@/components/form';
import { PageContainer, SimpleBanner } from '@/components/landing';
import { ContactRequestI } from '@/interfaces/contact';
import dynamic from 'next/dynamic';

interface KeyValue {
	[key: string]: any;
}

const Recaptcha = dynamic(
	() => import('@/components/common/recaptcha/ReCaptchaComponent'),
	{
		ssr: false,
	}
);

const rules = {
	firstName: {
		required: {
			value: true,
			message: 'Please provide the required information. ',
		},
		pattern: {
			value: /^[A-Za-z]+$/i,
			message: 'Only alphabetic characters accepted',
		},
		maxLength: {
			value: 35,
			message: 'Your first name has to have less than 35 characters',
		},
	},
	lastName: {
		required: {
			value: true,
			message: 'Please provide the required information. ',
		},
		pattern: {
			value: /^[A-Za-z]+$/i,
			message: 'Only alphabetic characters accepted',
		},
		maxLength: {
			value: 35,
			message: 'Your last name has to have less than 35 characters',
		},
	},
	email: {
		required: {
			value: true,
			message: 'Please provide the required information. ',
		},
	},
	phoneNumber: {
		required: {
			value: true,
			message: 'Please provide the required information. ',
		},
	},
	companyName: {
		maxLength: {
			value: 60,
			message: 'Your company name has to have less than 60 characters',
		},
	},
	subject: {
		required: {
			value: true,
			message: 'Please provide the required information. ',
		},
		maxLength: {
			value: 150,
			message: 'Your subject has to have less than 150 characters',
		},
	},
	message: {
		required: {
			value: true,
			message: 'Please provide the required information. ',
		},
		maxLength: {
			value: 1500,
			message: 'Your message has to have less than 1500 characters',
		},
	},
	agree: {
		required: {
			value: true,
			message: 'Please check the required box. ',
		},
	},
};

export const ContactUs: React.FC = () => {
	const [successMessage, setSuccessMessage] = useState<string>();
	const {
		register,
		handleSubmit,
		formState: { errors },
		setValue,
		reset,
	} = useForm({
		mode: 'onSubmit',
	});
	const [recaptchaValid, setRecaptchaValid] = useState<boolean | null>(null);

	const onSubmit = async (data: FieldValues): Promise<void> => {
		const request = getParsedData(data);
		const { data: response, statusCode } = await contactService.create(
			request as ContactRequestI
		);
		if (statusCode === 201) {
			setSuccessMessage(response?.message);
			scrollToTop();
			reset({});
		}
	};

	const onChangeRecaptcha = () => {
		setRecaptchaValid(true);
	};

	const getParsedData = (data: FieldValues): FieldValues => {
		return Object.entries(data)
			.filter(([, value]) => typeof value === 'boolean' || !!value)
			.reduce((acc, [key, value]) => {
				acc[key] = value;
				return acc;
			}, {} as KeyValue);
	};

	const scrollToTop = (): void => {
		window.scrollTo({
			top: 0,
			behavior: 'smooth',
		});
	};

	return (
		<>
			<SimpleBanner
				text="Contact Us"
				description="Have Questions? Get in Touch"
			/>
			<PageContainer
				className={`!mt-0 ${successMessage ? '!max-w-fit' : '!max-w-2xl'}`}
			>
				{successMessage ? (
					<div className="flex flex-col justify-center space-y-4 pt-[90px] lg:pb-[40px] 2xl:pb-[20px] lg:pt-[180px] 3xl:pb-[100px]">
						<Typography type="headline-2" className="text-center">
							Thank you for contacting us!
						</Typography>
						<Typography type="regular-28" className="text-center leading-10">
							{`We've received your message and will get back to you`} <br />
							as soon as possible.
						</Typography>
					</div>
				) : (
					<form
						className="w-full space-y-6 lg:pb-[88px]"
						onSubmit={handleSubmit(onSubmit)}
					>
						<div className="flex flex-col md:flex-row items-center gap gap-6 w-full">
							<div className="flex-1 w-full">
								<InputText
									markAsRequired
									name="firstName"
									title="First Name"
									register={register}
									rules={rules.firstName}
									error={errors.firstName}
								/>
							</div>
							<div className="flex-1 w-full">
								<InputText
									markAsRequired
									name="lastName"
									title="Last Name"
									register={register}
									rules={rules.lastName}
									error={errors.lastName}
								/>
							</div>
						</div>
						<div className="flex flex-col md:flex-row items-start gap gap-6 w-full">
							<div className="flex-1 w-full">
								<InputEmail
									markAsRequired
									name="email"
									title="Email"
									register={register}
									rules={rules.email}
									error={errors.email}
									setValueCustom={setValue}
								/>
							</div>
							<div className="flex-1 w-full">
								<InputPhone
									name="phoneNumber"
									title="Phone Number"
									placeholder="Ex: 5550000000"
									register={register}
									error={errors.phoneNumber}
									setValueInput={setValue}
								/>
							</div>
						</div>
						<InputText
							name="companyName"
							title="Company Name"
							register={register}
							rules={rules.companyName}
							error={errors.companyName}
						/>
						<InputText
							markAsRequired
							name="subject"
							title="Subject"
							register={register}
							rules={rules.subject}
							error={errors.subject}
						/>
						<Textarea
							markAsRequired
							name="message"
							title="Message"
							register={register}
							placeholder="Let us know how we can help you"
							rules={rules.message}
							error={errors.message}
						/>
						<InputCheck
							name="agree"
							register={register}
							rules={rules.agree}
							error={errors.agree}
						>
							I agree to the{' '}
							<Link
								href="/privacy-policy"
								target="_blank"
								className="text-blue-link"
							>
								{' '}
								Privacy Policy.
							</Link>
						</InputCheck>
						<InputCheck name="receiveUpdates" register={register}>
							I wish to receive news and updates.
						</InputCheck>
						<Recaptcha
							onChangeRecaptcha={onChangeRecaptcha}
							siteKey={process.env.NEXT_PUBLIC_API_RECAPTCHA || ''}
						/>
						<Button
							decoration="primary"
							type="submit"
							size="medium"
							className="m-auto md:!w-[6.688rem]"
							disabled={!recaptchaValid}
						>
							Submit
						</Button>
					</form>
				)}
			</PageContainer>
		</>
	);
};
